/**
 * Client-seitige Berechnung der jährlichen Gesamtkosten (OPEX + AfA).
 * 
 * Duale Berechnung: Entspricht calculate_yearly_costs im Backend
 * (domain/costs/service.py). Shared Fixtures in
 * backend_python/tests/fixtures/shared_calculation_fixtures.json
 * stellen die Konsistenz sicher.
 * 
 * Inputs:
 *   - capex.items (für AfA-Berechnung)
 *   - opex.items (für OPEX pro Jahr, 4 Modi)
 *   - energyRevenues.eintraege (für Modus "prozent")
 *   - laufzeitJahre (Projektlaufzeit)
 */
import {
  Capex,
  CapexItem,
  Opex,
  OpexItem,
  LeaseTier,
  EnergyRevenuesResponse,
  YearlyCostsResponse,
  YearlyCostEntry,
} from '../../../../types';


/**
 * Ermittelt den Prozentsatz für ein Jahr basierend auf Pachtkostenstaffeln.
 * Entspricht _get_percentage_for_year im Backend.
 */
function getPercentageForYear(item: OpexItem, jahr: number): number {
  const tiers = item.lease_tiers ?? [];

  if (tiers.length === 0) {
    return item.prozent_satz ?? 0;
  }

  // Sortiert nach first_year (sollte bereits sortiert sein)
  const sorted = [...tiers].sort((a, b) => a.first_year - b.first_year);

  let applicable: LeaseTier | null = null;
  for (const tier of sorted) {
    if (tier.first_year <= jahr) {
      applicable = tier;
    } else {
      break;
    }
  }

  if (applicable) {
    return applicable.percentage;
  }

  return sorted.length > 0 ? sorted[0].percentage : (item.prozent_satz ?? 0);
}


/**
 * Berechnet die Kosten für eine OPEX-Position in einem bestimmten Jahr.
 * Entspricht _berechne_kosten_fuer_jahr im Backend.
 */
function berechneKostenFuerJahr(
  item: OpexItem,
  jahr: number,
  erloesFuerJahr: number
): number {
  const modus = item.modus ?? 'steigend';
  const basiswert = item.basiswert ?? 0;

  switch (modus) {
    case 'steigend': {
      const steigerung = item.steigerung ?? 2.0;
      const faktor = Math.pow(1 + steigerung / 100, jahr - 1);
      return basiswert * faktor;
    }

    case 'manuell': {
      const manualValues = item.manual_values ?? [];
      const entry = manualValues.find(mv => mv.jahr === jahr);
      return entry?.wert ?? 0;
    }

    case 'prozent': {
      const prozentSatz = getPercentageForYear(item, jahr);
      if (erloesFuerJahr > 0 && prozentSatz > 0) {
        return erloesFuerJahr * prozentSatz / 100;
      }
      return 0;
    }

    default:
      return 0;
  }
}


/**
 * Berechnet die lineare Abschreibung für ein CAPEX-Item in einem Jahr.
 * Entspricht _berechne_abschreibung im Backend.
 */
function berechneAbschreibung(
  itemWert: number,
  abschreibungsdauer: number,
  bisherAbgeschrieben: number
): number {
  const restbuchwert = itemWert - bisherAbgeschrieben;
  if (restbuchwert <= 0) return 0;

  const linerarerSatz = abschreibungsdauer > 0 ? 100 / abschreibungsdauer : 5.0;
  const abschreibung = itemWert * linerarerSatz / 100;

  return Math.min(abschreibung, restbuchwert);
}


/**
 * Berechnet die kombinierten jährlichen Kosten (OPEX + Abschreibungen).
 * Entspricht calculate_yearly_costs im Backend (domain/costs/service.py).
 * 
 * @param capex - CAPEX mit allen Items (für AfA)
 * @param opex - OPEX mit allen Items (für Betriebskosten)
 * @param energyRevenues - Energieerlöse pro Jahr (für Modus "prozent")
 * @param laufzeitJahre - Projektlaufzeit in Jahren
 * @returns YearlyCostsResponse oder null falls keine Daten vorhanden
 */
export function calculateYearlyCosts(
  capex: Capex | null,
  opex: Opex | null,
  energyRevenues: EnergyRevenuesResponse | null,
  laufzeitJahre: number
): YearlyCostsResponse | null {
  if (!opex) return null;

  const effektiveLaufzeit = laufzeitJahre || 20;
  const opexItems = opex.items ?? [];
  const capexItems = capex?.items ?? [];

  // Erlöse pro Jahr als Lookup (jahr → erloes_eur)
  const erloeseByYear: Record<number, number> = {};
  if (energyRevenues?.eintraege) {
    for (const entry of energyRevenues.eintraege) {
      erloeseByYear[entry.jahr] = entry.erloes_eur;
    }
  }

  // is_editable Map (item_id → true wenn Modus manuell)
  const isEditableMap: Record<string, boolean> = {};
  for (const item of opexItems) {
    isEditableMap[item.id] = item.modus === 'manuell';
  }

  // AfA-Tracker: bisher abgeschrieben pro CAPEX-Item
  const bisherAbgeschriebenProItem: Record<string, number> = {};
  for (const item of capexItems) {
    bisherAbgeschriebenProItem[item.id] = 0;
  }

  const jahre: YearlyCostEntry[] = [];
  let summeBetriebskostenGesamt = 0;
  let summeAbschreibungenGesamt = 0;

  for (let jahr = 1; jahr <= effektiveLaufzeit; jahr++) {
    // OPEX pro Jahr berechnen
    const kosten: Record<string, number> = {};
    let summeBetriebskosten = 0;
    const erloesJahr = erloeseByYear[jahr] ?? 0;

    for (const item of opexItems) {
      const kostenWert = berechneKostenFuerJahr(item, jahr, erloesJahr);
      kosten[item.id] = kostenWert;
      summeBetriebskosten += kostenWert;
    }

    // AfA pro Jahr berechnen (über alle CAPEX-Items)
    let jahresAbschreibung = 0;
    for (const item of capexItems) {
      const itemWert = item.wert ?? 0;
      if (itemWert <= 0) continue;

      const abschreibungsdauer = item.abschreibungsdauer ?? 16;
      const itemAbschreibung = berechneAbschreibung(
        itemWert,
        abschreibungsdauer,
        bisherAbgeschriebenProItem[item.id]
      );

      bisherAbgeschriebenProItem[item.id] += itemAbschreibung;
      jahresAbschreibung += itemAbschreibung;
    }

    jahre.push({
      jahr,
      kosten,
      summe_betriebskosten: summeBetriebskosten,
      abschreibungen: jahresAbschreibung,
      is_editable: isEditableMap,
    });

    summeBetriebskostenGesamt += summeBetriebskosten;
    summeAbschreibungenGesamt += jahresAbschreibung;
  }

  return {
    jahre,
    summe_betriebskosten_gesamt: summeBetriebskostenGesamt,
    summe_abschreibungen_gesamt: summeAbschreibungenGesamt,
  };
}
